import { useState, useEffect, useRef } from 'react';

const DEFAULT_APPEARANCE = {
    feed: { fontSize: 24, emoteSize: 48 },
    chat: { fontSize: 16, emoteSize: 32 },
    global: { fontSize: 16, emoteSize: 32 }
};

export const useAppearance = () => {
    const saveTimeoutRef = useRef(null);
    const [appearance, setAppearance] = useState(() => {
        try {
            const saved = localStorage.getItem('spark_appearance_v2'); // New key for v2
            if (saved) {
                // Merge with defaults to ensure structure integrity
                const parsed = JSON.parse(saved);
                return { ...DEFAULT_APPEARANCE, ...parsed };
            }
        } catch (e) {
            console.error('Failed to load appearance settings:', e);
        }
        return DEFAULT_APPEARANCE;
    });

    useEffect(() => {
        const handleChange = (e) => {
            try {
                // OPTIMIZATION: Use payload if available to avoid disk read
                if (e.detail) {
                    setAppearance(e.detail);
                } else {
                    // Fallback for cross-tab 'storage' events
                    const saved = localStorage.getItem('spark_appearance_v2');
                    if (saved) {
                        setAppearance(JSON.parse(saved));
                    }
                }
            } catch (error) { console.error('Failed to sync appearance:', error); }
        };

        window.addEventListener('appearance-changed-v2', handleChange);
        window.addEventListener('storage', handleChange);

        return () => {
            window.removeEventListener('appearance-changed-v2', handleChange);
            window.removeEventListener('storage', handleChange);
        };
    }, []);

    const updateAppearance = (scope, key, value) => {
        // Calculate new settings based on current state
        const currentScope = appearance[scope] || DEFAULT_APPEARANCE[scope];
        const newScope = { ...currentScope, [key]: value };
        const newSettings = { ...appearance, [scope]: newScope };

        // 1. Dispatch Immediate Update (Flux-like)
        // Check if CustomEvent is available (it is in browsers)
        const event = new CustomEvent('appearance-changed-v2', { detail: newSettings });
        window.dispatchEvent(event);

        // 2. Debounce Persistence (Save to disk later)
        if (saveTimeoutRef.current) clearTimeout(saveTimeoutRef.current);
        saveTimeoutRef.current = setTimeout(() => {
            localStorage.setItem('spark_appearance_v2', JSON.stringify(newSettings));
        }, 800);
    };

    const saveSettings = (newFullSettings) => {
        // Immediate Update
        window.dispatchEvent(new CustomEvent('appearance-changed-v2', { detail: newFullSettings }));
        // Immediate Save
        localStorage.setItem('spark_appearance_v2', JSON.stringify(newFullSettings));
        setAppearance(newFullSettings);
    };

    const resetScope = (scope) => {
        const newSettings = { ...appearance, [scope]: DEFAULT_APPEARANCE[scope] };
        saveSettings(newSettings);
    };

    const resetAll = () => {
        saveSettings(DEFAULT_APPEARANCE);
    };

    return {
        appearance,
        updateAppearance,
        saveSettings, // Exported for bulk save
        resetScope,
        resetAll
    };
};
